import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Badge, Card, Input } from '@/components/ui';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { parseLocalDateTime } from '@/lib/date';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const EDGE_FN_URL = `${supabaseUrl}/functions/v1/admin-pre-approved-emails`;

interface PreApprovedRow {
  id: string;
  email: string;
  added_by: string;
  added_at: string;
  consumed_at: string | null;
  consumed_by: string | null;
}

// Same regex the edge function uses. Validating on the client too lets us
// disable the Add button up front instead of round-tripping a 400.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatDate(iso: string | null): string {
  if (!iso) return '--';
  return parseLocalDateTime(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export interface AdminPreApprovedProps {
  password: string;
  /**
   * Optional refresh hook the parent (AdminUsers) calls after a retro-approve
   * so its own users list reflects the flip without a manual refresh.
   */
  onConsumed?: () => void;
}

/**
 * Compact pre-approval section. Designed to live *inside* the Users tab —
 * adding/removing emails is the same mental model as approving users, so a
 * separate top-level tab is overkill. Consumed rows are tucked behind a
 * disclosure: they're audit history, not the daily workflow.
 */
export function AdminPreApproved({
  password,
  onConsumed,
}: AdminPreApprovedProps) {
  const [rows, setRows] = useState<PreApprovedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showConsumed, setShowConsumed] = useState(false);

  const pendingRows = useMemo(
    () => rows.filter((r) => r.consumed_at === null),
    [rows],
  );
  const consumedRows = useMemo(
    () => rows.filter((r) => r.consumed_at !== null),
    [rows],
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'GET',
        headers: { 'X-Admin-Password': password },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Unauthorized' }));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setRows((await res.json()) as PreApprovedRow[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load list');
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const inputIsValid = EMAIL_REGEX.test(emailInput.trim());

  const handleAdd = async () => {
    if (!inputIsValid) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'POST',
        headers: {
          'X-Admin-Password': password,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailInput.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as {
        row: PreApprovedRow | null;
        retro_approved: boolean;
        retro_approve_error?: string | null;
        already_approved?: boolean;
      };
      setEmailInput('');
      await fetchRows();
      if (data.already_approved) {
        // No row was inserted; user was already approved by another path
        // (invitation, manual approve, prior consume). Surface so the admin
        // doesn't expect a "pending" entry.
        setNotice('User is already approved — no pre-approval needed.');
      } else if (data.retro_approve_error) {
        // The pre-approval row was added but the retro-approve RPC failed;
        // explain the partial outcome so the admin can decide to retry.
        setError(
          `Added to pre-approved list, but the retro-approve step failed (${data.retro_approve_error}). The user will be approved on next sign-in.`,
        );
      } else {
        setNotice(
          data.retro_approved
            ? 'Added — user already had an unapproved account, approved now.'
            : 'Added to pre-approved list.',
        );
      }
      // If a retro-approve fired, the Users table above us is now stale.
      // Let the parent refetch so the user's "Pending" badge flips to
      // "Approved" without the admin reaching for Refresh.
      if (data.retro_approved && onConsumed) onConsumed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Add failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (email: string) => {
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'DELETE',
        headers: {
          'X-Admin-Password': password,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      // Refetch instead of an optimistic local filter. If the server-side
      // delete had a wider scope than expected (or, more importantly,
      // narrower — already consumed by the time we got there), this keeps
      // the UI in sync with truth instead of guessing.
      await fetchRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      variant="default"
      padding="md"
    >
      <div className="flex flex-col gap-3">
        {/* Heading */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <UserPlus
              size={16}
              className="text-muted-foreground"
            />
            <h2 className="text-sm font-semibold text-foreground">
              Pre-approve by email
            </h2>
            {pendingRows.length > 0 && (
              <Badge
                variant="warning"
                size="sm"
              >
                <Clock
                  size={10}
                  className="mr-1"
                />
                {pendingRows.length} pending sign-up
              </Badge>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Add an email to skip the post-onboarding approval gate. If the user
          has already signed up and is on the waitlist, they will be approved
          immediately.
        </p>

        {/* Add input */}
        <div className="flex flex-col gap-2 md:flex-row">
          <Input
            type="email"
            placeholder="user@example.com"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inputIsValid) void handleAdd();
            }}
            className="flex-1"
          />
          <Button
            onClick={() => void handleAdd()}
            loading={submitting}
            disabled={!inputIsValid || submitting}
            size="sm"
            leftIcon={<UserPlus size={14} />}
          >
            Pre-approve
          </Button>
        </div>

        {error ? (
          <div
            role="alert"
            className="px-3 py-2 bg-danger-light border border-danger rounded-md text-danger text-xs"
          >
            {error}
          </div>
        ) : null}

        {notice ? (
          <div
            role="status"
            className="px-3 py-2 bg-success-light border border-success rounded-md text-success text-xs"
          >
            {notice}
          </div>
        ) : null}

        {/* Pending list */}
        {loading && rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">Loading...</p>
        ) : pendingRows.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No emails waiting to be consumed.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {pendingRows.map((row) => (
              <li
                key={row.id}
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-muted/40"
              >
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <span className="text-sm font-medium text-foreground truncate">
                    {row.email}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Added {formatDate(row.added_at)} by {row.added_by}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={submitting}
                  leftIcon={<Trash2 size={12} />}
                  onClick={() => void handleRemove(row.email)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}

        {/* Consumed history (collapsed by default — audit, not daily use) */}
        {consumedRows.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              onClick={() => setShowConsumed((v) => !v)}
              className="self-start inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              aria-expanded={showConsumed}
            >
              {showConsumed ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronRight size={12} />
              )}
              <CheckCircle2 size={12} />
              {consumedRows.length} consumed (audit history)
            </button>
            {showConsumed && (
              <ul className="flex flex-col gap-1 pl-4 border-l border-border">
                {consumedRows.map((row) => (
                  <li
                    key={row.id}
                    className="flex items-baseline justify-between gap-2 text-xs"
                  >
                    <span className="text-foreground truncate">
                      {row.email}
                    </span>
                    <span className="text-muted-foreground whitespace-nowrap">
                      consumed {formatDate(row.consumed_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
